import React, { useState, useEffect } from "react";
import { Plus, Search, Trash2, Edit2 } from "lucide-react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axios from "axios";
import ApiUrl from "../Environment/ApiUrl";

const StocksTable = () => {
  // State management
  const [stocksData, setStocksData] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [deleteShowModal, setDeleteShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updateModal, setUpdateModal] = useState(false);
  const [updateId, setUpdateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form data states
  const initialFormData = {
    product_name: "",
    hsn_code: "",
    vehicle_type: "",
    type: "",
    tyre_size: "",
    category: "",
    subcategory: "",
    purchase_rate: "",
    avg_purchase_rate: "",
    available_quantity: "",
    total_purchase_quantity: "",
    sold_quantity: "",
    total_purchases: ""
  };

  const [stockFormData, setStockFormData] = useState(initialFormData);
  const [updateData, setUpdateData] = useState(initialFormData);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredStocks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentStocks = filteredStocks.slice(startIndex, startIndex + itemsPerPage);

  // Fetch data from API
  useEffect(() => {
    fetchData();
  }, []);

  // Filter stocks when search term changes
  useEffect(() => {
    const filtered = stocksData.filter((stock) =>
      Object.values(stock)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
    setFilteredStocks(filtered);
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, stocksData]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${ApiUrl}/stock_data.php`);
      if (response.data?.status && response.data.data) {
        setStocksData(response.data.data);
      } else {
        setStocksData([]);
        setError(response.data?.message || "No data received from server");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch stock data. Please try again.");
      setStocksData([]);
    } finally {
      setLoading(false);
    }
  };

  // Form handlers
  const handleInputChange = (e, isUpdate = false) => {
    const { name, value } = e.target;
    if (isUpdate) {
      setUpdateData(prev => ({ ...prev, [name]: value }));
    } else {
      setStockFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${ApiUrl}/stock_data.php`, stockFormData);
      if (response.data.status) {
        setStockFormData(initialFormData);
        fetchData();
        setShowModal(false);
      } else {
        setError(response.data.message || "Failed to create stock item");
      }
    } catch (error) {
      console.error("Error creating stock:", error);
      setError(error.response?.data?.message || "Failed to create stock item");
    }
  };

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setDeleteShowModal(true);
  };

  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${ApiUrl}/stock_data.php`, {
        data: { id: deleteId }
      });
      if (response.data.status) {
        setDeleteShowModal(false);
        fetchData();
      } else {
        setError(response.data.message || "Failed to delete stock item");
      }
    } catch (error) {
      console.error("Error deleting stock:", error);
      setError(error.response?.data?.message || "Failed to delete stock item");
    }
  };

  const handleUpdate = (id, data) => {
    setUpdateId(id);
    setUpdateData(data);
    setUpdateModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${ApiUrl}/stock_data.php`, {
        id: updateId,
        ...updateData
      });
      if (response.data.status) {
        setUpdateModal(false);
        fetchData();
      } else {
        setError(response.data.message || "Failed to update stock item");
      }
    } catch (error) {
      console.error("Error updating stock:", error);
      setError(error.response?.data?.message || "Failed to update stock item");
    }
  };

  // Pagination controls
  const renderPagination = () => {
    if (itemsPerPage >= filteredStocks.length) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = 1;

    if (totalPages > maxVisiblePages) {
      startPage = Math.max(1, Math.min(
        currentPage - Math.floor(maxVisiblePages / 2),
        totalPages - maxVisiblePages + 1
      ));
    }

    for (let i = 0; i < Math.min(maxVisiblePages, totalPages); i++) {
      const pageNum = startPage + i;
      pages.push(
        <button
          key={pageNum}
          onClick={() => setCurrentPage(pageNum)}
          className={`btn btn-sm ${pageNum === currentPage ? "bg-danger bg-opacity-10 text-danger" : "bg-white text-dark"}`}
          style={{ width: "32px", height: "32px" }}
        >
          {pageNum}
        </button>
      );
    }

    return (
      <div className="card-footer bg-white d-flex justify-content-between align-items-center border-top">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className={`btn btn-sm d-flex align-items-center gap-2 ${currentPage === 1 ? "bg-light text-muted" : "bg-white text-dark"}`}
        >
          <FaChevronLeft size={14} />
          <span className="ms-1 d-none d-md-inline">Previous</span>
        </button>

        <div className="d-flex gap-1">
          {currentPage > Math.floor(maxVisiblePages / 2) + 1 && totalPages > maxVisiblePages && (
            <>
              <button
                onClick={() => setCurrentPage(1)}
                className={`btn btn-sm ${1 === currentPage ? "bg-danger bg-opacity-10 text-danger" : "bg-white text-dark"}`}
                style={{ width: "32px", height: "32px" }}
              >
                1
              </button>
              <span className="px-2 py-1">...</span>
            </>
          )}

          {pages}

          {currentPage < totalPages - Math.floor(maxVisiblePages / 2) && totalPages > maxVisiblePages && (
            <>
              <span className="px-2 py-1">...</span>
              <button
                onClick={() => setCurrentPage(totalPages)}
                className={`btn btn-sm ${totalPages === currentPage ? "bg-danger bg-opacity-10 text-danger" : "bg-white text-dark"}`}
                style={{ width: "32px", height: "32px" }}
              >
                {totalPages}
              </button>
            </>
          )}
        </div>

        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className={`btn btn-sm d-flex align-items-center gap-2 ${currentPage === totalPages ? "bg-light text-muted" : "bg-white text-dark"}`}
        >
          <span className="ms-1 d-none d-md-inline">Next</span>
          <FaChevronRight size={14} />
        </button>
      </div>
    );
  };

  return (
    <div className="bg-white font-sans">
      {/* Error Display */}
      {error && (
        <div className="alert alert-danger mx-3 mx-md-4 mt-3" role="alert">
          {error}
        </div>
      )}

      {/* Header Section */}
      <div className="p-3 p-md-4 bg-white">
        <div className="d-flex justify-content-between align-items-center">
          <h2 className="m-0 fs-5 fw-semibold text-dark">Stocks</h2>
          <button
            className="btn btn-danger d-flex align-items-center gap-2"
            onClick={() => setShowModal(true)}
          >
            <Plus size={18} />
            Add Stock
          </button>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="card mx-3 mx-md-4 border">
        {/* Table Header with Controls */}
        <div className="card-header bg-white d-flex justify-content-between align-items-center p-3 border-bottom">
          <div className="d-flex align-items-center gap-2" style={{ height: "36px", width: "120px" }}>
            <div className="d-flex align-items-center justify-content-center" style={{ height: "28px", width: "14px" }}>
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                <rect x="1" y="1" width="3" height="3" fill="#666" />
                <rect x="1" y="5" width="3" height="3" fill="#666" />
                <rect x="1" y="9" width="3" height="2" fill="#666" />
                <rect x="5" y="1" width="6" height="1" fill="#666" />
                <rect x="5" y="3" width="6" height="1" fill="#666" />
                <rect x="5" y="5" width="6" height="1" fill="#666" />
                <rect x="5" y="7" width="6" height="1" fill="#666" />
                <rect x="5" y="9" width="6" height="1" fill="#666" />
              </svg>
            </div>
            <select
              className="border-0 bg-transparent text-dark form-select form-select-sm"
              style={{
                boxShadow: "none",
                outline: "none",
                lineHeight: "1.1",
                height: "28px",
                cursor: "pointer",
                width: "80px",
                paddingTop: "0",
                paddingBottom: "0",
              }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="5">Show 5</option>
              <option value="10">Show 10</option>
              <option value="25">Show 25</option>
              <option value={filteredStocks.length}>Show All</option>
            </select>
          </div>

          <div className="position-relative" style={{ width: "280px" }}>
            <input
              type="text"
              placeholder="Search"
              className="w-100 ps-3 pe-5 py-2 border rounded form-control"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              size={18}
              className="position-absolute top-50 end-0 translate-middle-y me-3 text-danger"
              style={{ pointerEvents: "none" }}
            />
          </div>
        </div>

        {/* Table Body */}
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-danger" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover mb-0 text-center">
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">S.No</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">Product Name</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">HSN Code</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">Category</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">Vehicle Type</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">Type</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">Tyre Size</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">Total Qty</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">Sold Qty</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">Available Qty</th>
                      <th style={{ backgroundColor: "#d3d3d3" }} className="p-3 fw-semibold small">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentStocks.length > 0 ? (
                      currentStocks.map((item, index) => (
                        <tr key={item.id} style={{ fontSize: "14px" }}>
                          <td>{startIndex + index + 1}</td>
                          <td>{item.product_name}</td>
                          <td>{item.hsn_code}</td>
                          <td>{item.category}</td>
                          <td>{item.vehicle_type}</td>
                          <td>{item.type}</td>
                          <td>{item.tyre_size}</td>
                          <td>{item.total_purchase_quantity}</td>
                          <td>{item.sold_quantity}</td>
                          <td>{item.available_quantity}</td>
                          <td className="small">
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                className="p-1 border-0 bg-transparent cursor-pointer text-muted"
                                onClick={() => handleOpenDeleteModal(item.id)}
                                aria-label="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                              <button
                                className="p-1 border-0 bg-transparent cursor-pointer text-muted"
                                onClick={() => handleUpdate(item.id, item)}
                                aria-label="Edit"
                              >
                                <Edit2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={11} className="text-center p-5">
                          {searchTerm ? "No matching records found" : "No stock data available"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </>
          )}
        </div>
      </div>

      {/* Add Stock Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title m-0 fw-semibold">Add Stock</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowModal(false);
                    setStockFormData(initialFormData);
                  }}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {Object.entries(stockFormData).map(([key, value]) => (
                      <div className="col-md-6" key={key}>
                        <label className="form-label small fw-medium">
                          {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          {(key === 'product_name' || key === 'hsn_code') && <span className="text-danger">*</span>}
                        </label>
                        <input
                          type={key.includes('quantity') || key.includes('purchases') ? "number" :
                               key.includes('rate') ? "number" : "text"}
                          step={key.includes('rate') ? "0.01" : undefined}
                          className="form-control form-control-sm"
                          name={key}
                          value={value}
                          onChange={(e) => handleInputChange(e)}
                          placeholder={`Enter ${key.split('_').join(' ')}`}
                          required={key === 'product_name' || key === 'hsn_code'}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="modal-footer border-top-0 pt-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        setShowModal(false);
                        setStockFormData(initialFormData);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-sm btn-primary">Save</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteShowModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center p-4">
                <div className="mb-3">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="#FEE2E2" stroke="#DC2626" strokeWidth="2"/>
                    <path d="M12 8V12" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 16H12.01" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h4 className="mb-3">Are you sure?</h4>
                <p className="mb-4">This action cannot be undone. The stock item will be permanently deleted.</p>
                <div className="d-flex justify-content-center gap-3">
                  <button type="button" className="btn btn-secondary" onClick={() => setDeleteShowModal(false)}>Cancel</button>
                  <button type="button" className="btn btn-danger" onClick={handleDelete}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Stock Modal */}
      {updateModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title m-0 fw-semibold">Update Stock</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setUpdateModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleUpdateSubmit}>
                  <div className="row g-3">
                    {Object.entries(updateData).map(([key, value]) => (
                      <div className="col-md-6" key={key}>
                        <label className="form-label small fw-medium">
                          {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          {(key === 'product_name' || key === 'hsn_code') && <span className="text-danger">*</span>}
                        </label>
                        <input
                          type={key.includes('quantity') || key.includes('purchases') ? "number" :
                               key.includes('rate') ? "number" : "text"}
                          step={key.includes('rate') ? "0.01" : undefined}
                          className="form-control form-control-sm"
                          name={key}
                          value={value}
                          onChange={(e) => handleInputChange(e, true)}
                          placeholder={`Enter ${key.split('_').join(' ')}`}
                          required={key === 'product_name' || key === 'hsn_code'}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="modal-footer border-top-0 pt-3">
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setUpdateModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-sm btn-primary">Update</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StocksTable;

// <?php
// /**
//  * stock_data.php - API endpoint for stock management (CRUD operations)
//  */

// ini_set('display_errors', 1);
// ini_set('display_startup_errors', 1);
// error_reporting(E_ALL);

// ini_set('log_errors', 1);
// ini_set('error_log', __DIR__ . '/error.log');

// header("Access-Control-Allow-Origin: *");
// header("Access-Control-Allow-Methods: GET, POST, PUT, DELETE, OPTIONS");
// header("Access-Control-Allow-Headers: Content-Type, Authorization, X-Requested-With");
// header("Content-Type: application/json; charset=utf-8");

// if ($_SERVER['REQUEST_METHOD'] === 'OPTIONS') {
//     http_response_code(200);
//     exit;
// }

// $conn = null;
// require_once __DIR__ . '/config.php';

// if (!isset($conn) || $conn->connect_error) {
//     $errorMessage = "Database connection failed: " . ($conn->connect_error ?? 'Unknown error');
//     error_log($errorMessage);
//     http_response_code(500);
//     echo json_encode([
//         "code" => 500,
//         "status" => false,
//         "message" => "Internal Server Error: " . $errorMessage
//     ]);
//     exit;
// }

// try {
//     $requestMethod = $_SERVER['REQUEST_METHOD'];
//     $requestUri = parse_url($_SERVER['REQUEST_URI'], PHP_URL_PATH);
//     $scriptPath = dirname($_SERVER['SCRIPT_NAME']);

//     // Extract ID from URL more reliably
//     $requestPath = str_replace($scriptPath, '', $requestUri);
//     $id = trim($requestPath, '/');

//     // Debug logging
//     error_log("Request Method: " . $requestMethod);
//     error_log("Request URI: " . $requestUri);
//     error_log("Script Path: " . $scriptPath);
//     error_log("Extracted ID: " . $id);

//     switch ($requestMethod) {
//         case 'GET':
//             if (!empty($id) && $id !== 'stock_data.php') {
//                 getStockItem($id);
//             } else {
//                 getStockData();
//             }
//             break;
//         case 'POST':
//             createStockItem();
//             break;
//         case 'PUT':
//             $input = json_decode(file_get_contents('php://input'), true);

//     // Try to get ID from URL first, then from request body
//     $requestUri = parse_url($_SERVER['REQUEST_URI'], PHP_URL_PATH);
//     $uriParts = explode('/', $requestUri);
//     $idFromUrl = end($uriParts);

//     $id = (!empty($idFromUrl) && is_numeric($idFromUrl) && $idFromUrl !== 'stock_data.php')
//         ? $idFromUrl
//         : ($input['id'] ?? null);

//     if (!empty($id) && is_numeric($id)) {
//         updateStockItem($id);
//     } else {
//         http_response_code(400);
//         echo json_encode([
//             "code" => 400,
//             "status" => false,
//             "message" => "Valid Stock ID is required for update"
//         ]);
//     }
//             break;
//     case 'DELETE':
//     $input = json_decode(file_get_contents('php://input'), true);
//     $id = $input['id'] ?? null;

//     if (!empty($id) && $id !== 'stock_data.php') {
//         deleteStockItem($id);
//     } else {
//         http_response_code(400);
//         echo json_encode([
//             "code" => 400,
//             "status" => false,
//             "message" => "Stock ID is required for deletion"
//         ]);
//     }
//     break;
//         default:
//             http_response_code(405);
//             echo json_encode([
//                 "code" => 405,
//                 "status" => false,
//                 "message" => "Method not allowed. Supported methods: GET, POST, PUT, DELETE"
//             ]);
//     }
// } catch (Exception $e) {
//     error_log("Main script error: " . $e->getMessage());
//     http_response_code(500);
//     echo json_encode([
//         "code" => 500,
//         "status" => false,
//         "message" => "Internal Server Error: " . $e->getMessage()
//     ]);
//     exit;
// }

// function getStockData() {
//     global $conn;

//     try {
//         // Get filter parameters from GET or POST
//         $filters = [];
//         $input = $_SERVER['REQUEST_METHOD'] === 'POST' ? json_decode(file_get_contents('php://input'), true) : $_GET;

//         $filters = [
//             'product_name' => $input['product_name'] ?? '',
//             'hsn_code' => $input['hsn_code'] ?? '',
//             'vehicle_type' => $input['vehicle_type'] ?? '',
//             'type' => $input['type'] ?? '',
//             'tyre_size' => $input['tyre_size'] ?? '',
//             'category' => $input['category'] ?? '',
//             'subcategory' => $input['subcategory'] ?? '',
//         ];

//         // Sanitize filter inputs
//         foreach ($filters as $key => $value) {
//             if (is_string($value)) {
//                 $filters[$key] = htmlspecialchars($value, ENT_QUOTES, 'UTF-8');
//             }
//         }

//         // Base SQL query
//         $sql = "
//             SELECT
//                 s.id,
//                 s.product_name,
//                 s.hsn_code,
//                 s.vehicle_type,
//                 s.type,
//                 s.tyre_size,
//                 s.category,
//                 s.subcategory,
//                 s.purchase_rate,
//                 s.avg_purchase_rate,
//                 s.total_purchase_quantity,
//                 s.sold_quantity,
//                 s.total_purchases
//             FROM stock_data s
//             WHERE 1=1
//         ";

//         $params = [];
//         $paramTypes = "";

//         // Build WHERE clauses
//         foreach ($filters as $key => $value) {
//             if (!empty($value)) {
//                 $sql .= " AND s.$key LIKE ?";
//                 $params[] = '%' . $value . '%';
//                 $paramTypes .= "s";
//             }
//         }

//         $sql .= " ORDER BY s.product_name";

//         $stmt = $conn->prepare($sql);
//         if (!$stmt) {
//             throw new Exception("SQL prepare failed: " . $conn->error);
//         }

//         if (!empty($params)) {
//             $stmt->bind_param($paramTypes, ...$params);
//         }

//         $stmt->execute();
//         $result = $stmt->get_result();

//         $stocks = [];
//         while ($row = $result->fetch_assoc()) {
//             // Calculate available quantity
//             $available_quantity = (int)$row['total_purchase_quantity'] - (int)$row['sold_quantity'];
//             if ($available_quantity < 0) {
//                 $available_quantity = 0;
//             }

//             $stocks[] = [
//                 'id' => (int)$row['id'],
//                 'product_name' => $row['product_name'],
//                 'hsn_code' => $row['hsn_code'],
//                 'vehicle_type' => $row['vehicle_type'],
//                 'type' => $row['type'],
//                 'tyre_size' => $row['tyre_size'],
//                 'category' => $row['category'],
//                 'subcategory' => $row['subcategory'],
//                 'purchase_rate' => (float)$row['purchase_rate'],
//                 'avg_purchase_rate' => (float)$row['avg_purchase_rate'],
//                 'available_quantity' => $available_quantity,
//                 'total_purchase_quantity' => (int)$row['total_purchase_quantity'],
//                 'sold_quantity' => (int)$row['sold_quantity'],
//                 'total_purchases' => (int)$row['total_purchases']
//             ];
//         }

//         if (count($stocks) > 0) {
//             http_response_code(200);
//             echo json_encode([
//                 "code" => 200,
//                 "status" => true,
//                 "message" => "Stock data retrieved successfully",
//                 "data" => $stocks,
//                 "total_products" => count($stocks),
//                 "filters_applied" => $filters
//             ], JSON_PRETTY_PRINT);
//         } else {
//             http_response_code(200);
//             echo json_encode([
//                 "code" => 200,
//                 "status" => true,
//                 "message" => "No stock data found matching the filters.",
//                 "data" => [],
//                 "total_products" => 0,
//                 "filters_applied" => $filters
//             ], JSON_PRETTY_PRINT);
//         }

//         $stmt->close();
//     } catch (Exception $e) {
//         error_log("getStockData error: " . $e->getMessage());
//         http_response_code(500);
//         echo json_encode([
//             "code" => 500,
//             "status" => false,
//             "message" => "Internal Server Error: " . $e->getMessage()
//         ]);
//     }
// }

// function getStockItem($id) {
//     global $conn;

//     try {
//         // Validate ID is numeric
//         if (!is_numeric($id)) {
//             http_response_code(400);
//             echo json_encode([
//                 "code" => 400,
//                 "status" => false,
//                 "message" => "Invalid stock ID format"
//             ]);
//             return;
//         }

//         $stmt = $conn->prepare("
//             SELECT
//                 id,
//                 product_name,
//                 hsn_code,
//                 vehicle_type,
//                 type,
//                 tyre_size,
//                 category,
//                 subcategory,
//                 purchase_rate,
//                 avg_purchase_rate,
//                 total_purchase_quantity,
//                 sold_quantity,
//                 total_purchases
//             FROM stock_data
//             WHERE id = ?
//         ");

//         if (!$stmt) {
//             throw new Exception("Prepare failed: " . $conn->error);
//         }

//         $stmt->bind_param("i", $id);
//         $stmt->execute();
//         $result = $stmt->get_result();

//         if ($result->num_rows === 0) {
//             http_response_code(404);
//             echo json_encode([
//                 "code" => 404,
//                 "status" => false,
//                 "message" => "Stock item not found"
//             ]);
//             return;
//         }

//         $stock = $result->fetch_assoc();

//         // Calculate available quantity
//         $available_quantity = (int)$stock['total_purchase_quantity'] - (int)$stock['sold_quantity'];
//         if ($available_quantity < 0) {
//             $available_quantity = 0;
//         }

//         $response = [
//             'id' => (int)$stock['id'],
//             'product_name' => $stock['product_name'],
//             'hsn_code' => $stock['hsn_code'],
//             'vehicle_type' => $stock['vehicle_type'],
//             'type' => $stock['type'],
//             'tyre_size' => $stock['tyre_size'],
//             'category' => $stock['category'],
//             'subcategory' => $stock['subcategory'],
//             'purchase_rate' => (float)$stock['purchase_rate'],
//             'avg_purchase_rate' => (float)$stock['avg_purchase_rate'],
//             'available_quantity' => $available_quantity,
//             'total_purchase_quantity' => (int)$stock['total_purchase_quantity'],
//             'sold_quantity' => (int)$stock['sold_quantity'],
//             'total_purchases' => (int)$stock['total_purchases']
//         ];

//         http_response_code(200);
//         echo json_encode([
//             "code" => 200,
//             "status" => true,
//             "message" => "Stock item retrieved successfully",
//             "data" => $response
//         ], JSON_PRETTY_PRINT);

//     } catch (Exception $e) {
//         error_log("getStockItem error: " . $e->getMessage());
//         http_response_code(500);
//         echo json_encode([
//             "code" => 500,
//             "status" => false,
//             "message" => "Internal Server Error: " . $e->getMessage()
//         ]);
//     }
// }

// function createStockItem() {
//     global $conn;

//     try {
//         $input = json_decode(file_get_contents('php://input'), true);

//         if (empty($input['product_name']) || empty($input['hsn_code'])) {
//             http_response_code(400);
//             echo json_encode([
//                 "code" => 400,
//                 "status" => false,
//                 "message" => "Product name and HSN code are required"
//             ]);
//             return;
//         }

//         // Prepare data
//         $product_name = htmlspecialchars($input['product_name'], ENT_QUOTES, 'UTF-8');
//         $hsn_code = htmlspecialchars($input['hsn_code'], ENT_QUOTES, 'UTF-8');
//         $vehicle_type = htmlspecialchars($input['vehicle_type'] ?? '', ENT_QUOTES, 'UTF-8');
//         $type = htmlspecialchars($input['type'] ?? '', ENT_QUOTES, 'UTF-8');
//         $tyre_size = htmlspecialchars($input['tyre_size'] ?? '', ENT_QUOTES, 'UTF-8');
//         $category = htmlspecialchars($input['category'] ?? '', ENT_QUOTES, 'UTF-8');
//         $subcategory = htmlspecialchars($input['subcategory'] ?? '', ENT_QUOTES, 'UTF-8');
//         $purchase_rate = floatval($input['purchase_rate'] ?? 0);
//         $avg_purchase_rate = floatval($input['avg_purchase_rate'] ?? $purchase_rate);
//         $total_purchase_quantity = intval($input['total_purchase_quantity'] ?? 0);
//         $sold_quantity = intval($input['sold_quantity'] ?? 0);
//         $total_purchases = intval($input['total_purchases'] ?? 1);

//         // Calculate available quantity
//         $available_quantity = max(0, $total_purchase_quantity - $sold_quantity);

//         $stmt = $conn->prepare("
//             INSERT INTO stock_data (
//                 product_name, hsn_code, vehicle_type, type, tyre_size, category, subcategory,
//                 purchase_rate, avg_purchase_rate, available_quantity, total_purchase_quantity,
//                 sold_quantity, total_purchases
//             ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
//         ");

//         if (!$stmt) {
//             throw new Exception("Prepare failed: " . $conn->error);
//         }

//         $stmt->bind_param("sssssssddiiii",
//             $product_name, $hsn_code, $vehicle_type, $type, $tyre_size, $category, $subcategory,
//             $purchase_rate, $avg_purchase_rate, $available_quantity, $total_purchase_quantity,
//             $sold_quantity, $total_purchases
//         );

//         if ($stmt->execute()) {
//             $newId = $conn->insert_id;
//             http_response_code(201);
//             echo json_encode([
//                 "code" => 201,
//                 "status" => true,
//                 "message" => "Stock item created successfully",
//                 "id" => $newId
//             ]);
//         } else {
//             throw new Exception("Failed to create stock item: " . $stmt->error);
//         }
//     } catch (Exception $e) {
//         error_log("createStockItem error: " . $e->getMessage());
//         http_response_code(500);
//         echo json_encode([
//             "code" => 500,
//             "status" => false,
//             "message" => "Internal Server Error: " . $e->getMessage()
//         ]);
//     }
// }

// function updateStockItem($id) {
//     global $conn;

//     try {
//         $input = json_decode(file_get_contents('php://input'), true);

//         // if (!$input) {
//         //     http_response_code(400);
//         //     echo json_encode([
//         //         "code" => 400,
//         //         "status" => false,
//         //         "message" => "Invalid JSON input"
//         //     ]);
//         //     return;
//         // }

//         // // Validate ID is numeric
//         // if (!is_numeric($id)) {
//         //     http_response_code(400);
//         //     echo json_encode([
//         //         "code" => 400,
//         //         "status" => false,
//         //         "message" => "Invalid stock ID format"
//         //     ]);
//         //     return;
//         // }

//         // Prepare data
//         $product_name = htmlspecialchars($input['product_name'] ?? '', ENT_QUOTES, 'UTF-8');
//         $hsn_code = htmlspecialchars($input['hsn_code'] ?? '', ENT_QUOTES, 'UTF-8');
//         $vehicle_type = htmlspecialchars($input['vehicle_type'] ?? '', ENT_QUOTES, 'UTF-8');
//         $type = htmlspecialchars($input['type'] ?? '', ENT_QUOTES, 'UTF-8');
//         $tyre_size = htmlspecialchars($input['tyre_size'] ?? '', ENT_QUOTES, 'UTF-8');
//         $category = htmlspecialchars($input['category'] ?? '', ENT_QUOTES, 'UTF-8');
//         $subcategory = htmlspecialchars($input['subcategory'] ?? '', ENT_QUOTES, 'UTF-8');
//         $purchase_rate = floatval(str_replace(',', '', $input['purchase_rate'] ?? 0));
//         $avg_purchase_rate = floatval(str_replace(',', '', $input['avg_purchase_rate'] ?? 0));
//         $total_purchase_quantity = intval($input['total_purchase_quantity'] ?? 0);
//         $sold_quantity = intval($input['sold_quantity'] ?? 0);
//         $total_purchases = intval($input['total_purchases'] ?? 0);

//         // Calculate available quantity
//         $available_quantity = max($total_purchase_quantity - $sold_quantity, 0);

//         // Check if ID exists
//         $checkStmt = $conn->prepare("SELECT COUNT(*) FROM stock_data WHERE id = ?");
//         $checkStmt->bind_param("i", $id);
//         $checkStmt->execute();
//         $checkStmt->bind_result($count);
//         $checkStmt->fetch();
//         $checkStmt->close();

//         if ($count === 0) {
//             http_response_code(404);
//             echo json_encode([
//                 "code" => 404,
//                 "status" => false,
//                 "message" => "Stock item not found"
//             ]);
//             return;
//         }

//         // Update the stock item
//         $stmt = $conn->prepare("
//             UPDATE stock_data SET
//                 product_name = ?,
//                 hsn_code = ?,
//                 vehicle_type = ?,
//                 type = ?,
//                 tyre_size = ?,
//                 category = ?,
//                 subcategory = ?,
//                 purchase_rate = ?,
//                 avg_purchase_rate = ?,
//                 available_quantity = ?,
//                 total_purchase_quantity = ?,
//                 sold_quantity = ?,
//                 total_purchases = ?
//             WHERE id = ?
//         ");

//         if (!$stmt) {
//             throw new Exception("Prepare failed: " . $conn->error);
//         }

//         $stmt->bind_param(
//             "sssssssddiiiis",
//             $product_name, $hsn_code, $vehicle_type, $type, $tyre_size,
//             $category, $subcategory, $purchase_rate, $avg_purchase_rate,
//             $available_quantity, $total_purchase_quantity, $sold_quantity,
//             $total_purchases, $id
//         );

//         if ($stmt->execute()) {
//             if ($stmt->affected_rows > 0) {
//                 http_response_code(200);
//                 echo json_encode([
//                     "code" => 200,
//                     "status" => true,
//                     "message" => "Stock item updated successfully"
//                 ]);
//             } else {
//                 http_response_code(200);
//                 echo json_encode([
//                     "code" => 200,
//                     "status" => true,
//                     "message" => "No changes made to stock item"
//                 ]);
//             }
//         } else {
//             throw new Exception("Failed to update stock item: " . $stmt->error);
//         }

//     } catch (Exception $e) {
//         error_log("updateStockItem error: " . $e->getMessage());
//         http_response_code(500);
//         echo json_encode([
//             "code" => 500,
//             "status" => false,
//             "message" => "Internal Server Error: " . $e->getMessage()
//         ]);
//     }
// }

// function deleteStockItem($id) {
//     global $conn;

//     try {
//         // Validate ID is numeric
//         if (!is_numeric($id)) {
//             http_response_code(400);
//             echo json_encode([
//                 "code" => 400,
//                 "status" => false,
//                 "message" => "Invalid stock ID format"
//             ]);
//             return;
//         }

//         $stmt = $conn->prepare("DELETE FROM stock_data WHERE id = ?");

//         if (!$stmt) {
//             throw new Exception("Prepare failed: " . $conn->error);
//         }

//         $stmt->bind_param("i", $id);

//         if ($stmt->execute()) {
//             if ($stmt->affected_rows > 0) {
//                 http_response_code(200);
//                 echo json_encode([
//                     "code" => 200,
//                     "status" => true,
//                     "message" => "Stock item deleted successfully"
//                 ]);
//             } else {
//                 http_response_code(404);
//                 echo json_encode([
//                     "code" => 404,
//                     "status" => false,
//                     "message" => "Stock item not found"
//                 ]);
//             }
//         } else {
//             throw new Exception("Failed to delete stock item: " . $stmt->error);
//         }
//     } catch (Exception $e) {
//         error_log("deleteStockItem error: " . $e->getMessage());
//         http_response_code(500);
//         echo json_encode([
//             "code" => 500,
//             "status" => false,
//             "message" => "Internal Server Error: " . $e->getMessage()
//         ]);
//     }
// }

// function deleteStockItem($id) {
//     global $conn;

//     try {
//         // Validate ID is numeric
//         if (!is_numeric($id)) {
//             http_response_code(400);
//             echo json_encode([
//                 "code" => 400,
//                 "status" => false,
//                 "message" => "Invalid stock ID format"
//             ]);
//             return;
//         }

//         // First check if record exists
//         $check_stmt = $conn->prepare("SELECT id FROM stock_data WHERE id = ?");
//         if (!$check_stmt) {
//             throw new Exception("Prepare check failed: " . $conn->error);
//         }

//         $check_stmt->bind_param("i", $id);
//         $check_stmt->execute();
//         $check_stmt->store_result();

//         if ($check_stmt->num_rows === 0) {
//             http_response_code(404);
//             echo json_encode([
//                 "code" => 404,
//                 "status" => false,
//                 "message" => "Stock item with ID $id does not exist"
//             ]);
//             $check_stmt->close();
//             return;
//         }
//         $check_stmt->close();

//         // Proceed with deletion
//         $delete_stmt = $conn->prepare("DELETE FROM stock_data WHERE id = ?");

//         if (!$delete_stmt) {
//             throw new Exception("Prepare delete failed: " . $conn->error);
//         }

//         $delete_stmt->bind_param("i", $id);

//         if ($delete_stmt->execute()) {
//             if ($delete_stmt->affected_rows > 0) {
//                 http_response_code(200);
//                 echo json_encode([
//                     "code" => 200,
//                     "status" => true,
//                     "message" => "Stock item deleted successfully",
//                     "deleted_id" => $id
//                 ]);
//             } else {
//                 http_response_code(404);
//                 echo json_encode([
//                     "code" => 404,
//                     "status" => false,
//                     "message" => "No stock item was deleted (ID: $id)"
//                 ]);
//             }
//         } else {
//             throw new Exception("Failed to delete stock item: " . $delete_stmt->error);
//         }

//         $delete_stmt->close();
//     } catch (Exception $e) {
//         error_log("deleteStockItem error: " . $e->getMessage());
//         http_response_code(500);
//         echo json_encode([
//             "code" => 500,
//             "status" => false,
//             "message" => "Internal Server Error",
//             "error_details" => $e->getMessage()
//         ]);
//     }
// }
